import axios from 'axios';
import Validator from 'validatorjs';

import config from '_config';
import auth from './auth';

window.isAlreadyFetchingAccessToken = false;

class BaseService {
  getUrl() {
    return this.normalizeBaseUrl(config.API_URL);
  }

  normalizeBaseUrl(url = '') {
    return url.replace(/\/+$/, '');
  }

  normalizeRequestUrl(url = '') {
    return url.replace(/\/+(?=([?#]|$))/, '');
  }

  request(
    url = this.getUrl(),
    data = null,
    method = 'get',
    responseType = null
  ) {
    const params = method === 'get' ? data : {};
    const normalizedUrl = this.normalizeRequestUrl(url);

    return axios({
      method,
      url: normalizedUrl,
      data,
      params,
      responseType,
    })
      .then(response => this.handleResponse(response))
      .catch(this.handleError);
  }

  validate(data, rules, names = null) {
    const validation = new Validator(data, rules);

    if (names !== null) validation.setAttributeNames(names);

    if (validation.fails()) {
      Object.keys(validation.errors.all()).forEach(error =>
        Promise.reject(
          new Error({
            error_description: validation.errors.all()[error][0],
          })
        )
      );
    }

    return Promise.resolve();
  }

  createFormData(data, splitArray = false, arrayIndexProp = []) {
    const fd = new FormData();

    Object.keys(data).forEach(key => {
      const item = data[key];

      if (typeof item === 'boolean') {
        fd.append(key, item ? 1 : 0);
      } else if (splitArray && Array.isArray(item)) {
        const isIndexProp = arrayIndexProp.find(i => i === key);

        Object.keys(item).forEach(index => {
          fd.append(
            `${key}[${index}]${isIndexProp ? '[_id]' : ''}`,
            item[index]
          );
        });
      } else {
        fd.append(key, item);
      }
    });

    return fd;
  }

  handleResponse(response) {
    return Object.assign(response.data, {
      message: response.data.message || response.message,
    });
  }

  handleError(error) {
    if (error.response.status === 401) {
      if (!window.isAlreadyFetchingAccessToken) {
        const lastRoute = error.config;
        window.isAlreadyFetchingAccessToken = true;
        auth.refreshToken().then(response => {
          window.isAlreadyFetchingAccessToken = false;
          lastRoute.headers.Authorization = response.data.access_token;
          // retry the last request
          return axios(lastRoute);
        });
      }
      window.location.href = '/login';
    }
    if (error.response.status === 403) {
      auth.resetUser(error.response.data);
    }
    error.response.data.status = error.response.status;
    throw error.response ? error.response.data : error;
  }

  setParamsToIfPdf(pagination, pdf) {
    const parameters = {
      params: pagination,
      responseType: 'json',
    };

    if (pdf) {
      parameters.responseType = 'arraybuffer';
    }
    return parameters;
  }

  setFormData(object, formData, parent = '') {
    if (object) {
      Object.entries(object).map(field => {
        let label = field[0];
        if (parent !== '') {
          label = `${parent}[${field[0]}]`;
        }
        if (typeof field[1] === 'object' && !(field[1] instanceof File)) {
          return this.setFormData(field[1], formData, label);
        }
        formData.append(label, field[1]);
        return field;
      });
    }
    return formData;
  }

  setFiles(object, formData) {
    let fileIndex = 0;
    let imagesIndex = 0;
    Object.values(object.images).map(image => {
      if (image instanceof File) {
        formData.delete(`images[${fileIndex}]`);
        // eslint-disable-next-line no-return-assign
        return formData.append(`files[${(fileIndex += 1)}]`, image);
      }
      imagesIndex += 1;
      return Object.entries(image).forEach(key => {
        formData.append(`images[${imagesIndex}][${key[0]}]`, key[1]);
      });
    });
    return formData;
  }
}

export default BaseService;
